import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { PanelInterview } from './PanelInterview';
import {
  ApplicantParticulars,
  AcademicAssessments,
  PublicationAssessments,
  MeritAssessments,
  ScoresOnApplicant,
  DeclarationAssessments,
  AcademicRefereeConfirmation,
  RefereeTickMode,
} from '.';
import { DSRC_DECISION_TYPE } from '../constants';
import {
  useSubmitDsrcAssessment,
  useValidateDsrcAssessment,
  useGetNominationInformation,
  useRecomputeGrading,
} from '../../../graphql';
import { PAGE_TEXT } from '../../../lang';
import {
  AssessmentSidebar,
  ASSESSMENT_MODULE,
  ASSESSMENT_DETAIL_MODULE,
  ASSESSMENT_DETAIL_MAP_COMPETITIVE_VER,
  ASSESSMENT_DETAIL_MAP_OTHER_VER,
} from '../../../components';
import {
  getApplication,
  checkInvalidDate,
} from '../../../../Application/graphql';
import {
  type SidebarData,
  Button,
  Dialog,
  Loading,
  Alert,
  type AlertDetails,
  Stepper,
  STEPPER_LABELS,
  type Steps,
  type DialogDetails,
  UserRole,
  Accordion,
} from '../../../../../components';
import {
  Evaluation,
  EVALUATION_SCENE,
} from '../../../../Nomination/components';
import { APPLICATION_STATUS_TYPE } from '../../../../../utils/statusConstants/statusConstants';
import { FROZEN_STATUS } from '../../../../ScholarshipManagement/constants/fundingStatus';
import { Admission } from '../Admission';

const isUPSF = false; // hide old version for temp, in case any change
const sidebarModule: ASSESSMENT_MODULE[] = [
  ASSESSMENT_MODULE.PARTICULARS,
  ASSESSMENT_MODULE.DORPGS_ASSESSMENT,
  ASSESSMENT_MODULE.EVALUATION,
];
export const DRCAssessmentDetail: React.FunctionComponent = () => {
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [openAlert, setOpenAlert] = useState(false);
  const [activatedStep, setActivatedStep] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  // am means Activated Module
  let activatedModuleId = searchParams.get('am') as ASSESSMENT_MODULE;
  if (!sidebarModule.includes(activatedModuleId)) {
    activatedModuleId = ASSESSMENT_MODULE.DORPGS_ASSESSMENT;
    searchParams.set('am', activatedModuleId);
  }

  const {
    applicationData,
    remarks: returnedRemarks,
    loading: applicationDataLoading,
    error,
  } = getApplication(applicationId);

  const {
    academicQualifications,
    programmeChoice,
    publications,
    merits,
    status,
  } = applicationData;

  const { entryYear } = programmeChoice; // used for getting registration deadline

  /* if the application is not being assessed by dsrc, redirect to monitor */
  if (
    !applicationDataLoading &&
    status !== APPLICATION_STATUS_TYPE.UNDER_DORPGS
  ) {
    navigate(`/internal/monitor/${applicationId}`);
  }

  const {
    validateDsrcAssessment,
    assessedPubIds,
    assessedMeritIds,
    initializedAqObjs,
    loading: assessmentDetailLoading,
    error: assessmentDetailError,
    refetch: refetchDsrcAssessment,
  } = useValidateDsrcAssessment(applicationId);

  const { dsrcAdmissionForm, hasDoneArr } = validateDsrcAssessment;
  const publicationIds = publications?.map(item => item?.publicationId ?? '');
  const academicQualificationIds = academicQualifications
    .map(item => item.academicQualificationId ?? '')
    .filter(id => id !== '');

  const validAssessedPubIds = assessedPubIds.filter(id =>
    publicationIds.includes(id),
  );
  const validAssessedMeritIds = assessedMeritIds.filter(id =>
    merits.includes(id),
  );

  const validAssessedAqIds: string[] = initializedAqObjs
    ?.filter(
      obj =>
        obj.convertedGPA >= 0 &&
        academicQualificationIds.includes(obj.academicQualificationReferenceId),
    )
    ?.map(aq => aq.academicQualificationReferenceId);

  const {
    dsrcDecision, // undefined means have not finished GSB/1 (Admission Requirement)
  } = dsrcAdmissionForm;

  const admissionApproved = dsrcDecision === DSRC_DECISION_TYPE.OFFER_APPROVED;
  const admissionNotApproved = !admissionApproved && dsrcDecision !== undefined;

  const {
    nominationInformation,
    scholarshipNomination,
    isUPAF,
    isScholarshipInterviewFinished,
    loading: fundingLoading,
    error: fundingError,
    refetch: refetchNominationEvaluation,
  } = useGetNominationInformation({
    application: { _id: { eq: applicationId } },
  });

  const loading =
    assessmentDetailLoading || applicationDataLoading || fundingLoading;

  const recommendForHKPFS = nominationInformation?.recommendForHKPFS;

  const map = isUPAF
    ? ASSESSMENT_DETAIL_MAP_COMPETITIVE_VER
    : ASSESSMENT_DETAIL_MAP_OTHER_VER;

  const handleChangeActivated = (activatedId: string): void => {
    searchParams.set('am', activatedId);

    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const drcAssessmentValidation = [
    {
      condition:
        (isUPSF &&
          hasDoneArr.includes(
            ASSESSMENT_DETAIL_MODULE.ACADEMIC_QUALIFICATION,
          ) &&
          admissionApproved &&
          academicQualifications.length === validAssessedAqIds.length) ||
        admissionNotApproved ||
        !isUPSF,
      message:
        PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT
          .FILL_ACADEMIC_QUALIFICATION,
      stepKey: ASSESSMENT_DETAIL_MODULE.ACADEMIC_QUALIFICATION,
    },
    {
      condition:
        (isUPSF &&
          hasDoneArr.includes(
            ASSESSMENT_DETAIL_MODULE.PUBLICATION_ASSESSMENT,
          ) &&
          admissionApproved &&
          publications.length === validAssessedPubIds.length) ||
        admissionNotApproved ||
        !publications?.length ||
        !isUPSF,
      message:
        PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT.FILL_PUBLICATION,
      stepKey: ASSESSMENT_DETAIL_MODULE.PUBLICATION_ASSESSMENT,
    },
    {
      condition:
        (isUPSF &&
          hasDoneArr.includes(ASSESSMENT_DETAIL_MODULE.MERIT_ASSESSMENT) &&
          admissionApproved &&
          merits.length === validAssessedMeritIds.length) ||
        admissionNotApproved ||
        !merits?.length ||
        !isUPSF,
      message: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT.FILL_MERIT,
      stepKey: ASSESSMENT_DETAIL_MODULE.MERIT_ASSESSMENT,
    },
    {
      condition: hasDoneArr.includes(
        ASSESSMENT_DETAIL_MODULE.ADMISSION_REQUIREMENT,
      ),
      message: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT.FILL_GSB,
      stepKey: ASSESSMENT_DETAIL_MODULE.ADMISSION_REQUIREMENT,
    },
    {
      condition:
        (hasDoneArr.includes(ASSESSMENT_DETAIL_MODULE.REFEREE_ELIGIBILITY) &&
          admissionApproved) ||
        admissionNotApproved,
      message:
        PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT.FILL_REFEREE_CONFIRM,
      stepKey: ASSESSMENT_DETAIL_MODULE.REFEREE_ELIGIBILITY,
    },

    ...(isUPAF
      ? [
          {
            condition:
              (hasDoneArr.includes(ASSESSMENT_DETAIL_MODULE.INTERVIEW_PANEL) &&
                admissionApproved) ||
              admissionNotApproved,
            message:
              PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT
                .FILL_PANEL_MEMBER,
            stepKey: ASSESSMENT_DETAIL_MODULE.INTERVIEW_PANEL,
          },
        ]
      : []),
    {
      condition:
        (hasDoneArr.includes(ASSESSMENT_DETAIL_MODULE.DECLARATION) &&
          admissionApproved) ||
        admissionNotApproved,
      message:
        PAGE_TEXT.DRC_ASSESSMENT_DETAILS.VALIDATION_SUBMIT.FILL_DECLARATION,
      stepKey: ASSESSMENT_DETAIL_MODULE.DECLARATION,
    },
  ];

  const assessmentSteps: Steps[] = [
    ...(isUPSF
      ? [
          {
            label: STEPPER_LABELS.ACADEMIC_QUALIFICATION.LABEL,
            description: STEPPER_LABELS.ACADEMIC_QUALIFICATION.DESCRIPTION,
            hasDone:
              hasDoneArr.includes(
                ASSESSMENT_DETAIL_MODULE.ACADEMIC_QUALIFICATION,
              ) && academicQualifications.length === validAssessedAqIds.length,
          },
          {
            label: STEPPER_LABELS.PUBLICATION.LABEL,
            description: STEPPER_LABELS.PUBLICATION.DESCRIPTION,
            hasDone:
              (hasDoneArr.includes(
                ASSESSMENT_DETAIL_MODULE.PUBLICATION_ASSESSMENT,
              ) &&
                publications.length === validAssessedPubIds.length) ||
              !publications?.length,
          },
          {
            label: STEPPER_LABELS.MERIT.LABEL,
            description: STEPPER_LABELS.MERIT.DESCRIPTION,
            hasDone:
              (hasDoneArr.includes(ASSESSMENT_DETAIL_MODULE.MERIT_ASSESSMENT) &&
                merits.length === validAssessedMeritIds.length) ||
              !merits?.length,
          },
          {
            label: STEPPER_LABELS.SCORES_ON_APPLICANT.LABEL,
            description: STEPPER_LABELS.SCORES_ON_APPLICANT.DESCRIPTION,
            hasDone:
              hasDoneArr.includes(
                ASSESSMENT_DETAIL_MODULE.ACADEMIC_QUALIFICATION,
              ) &&
              academicQualifications.length === validAssessedAqIds.length &&
              ((hasDoneArr.includes(
                ASSESSMENT_DETAIL_MODULE.PUBLICATION_ASSESSMENT,
              ) &&
                publications.length === validAssessedPubIds.length) ||
                !publications?.length) &&
              ((hasDoneArr.includes(
                ASSESSMENT_DETAIL_MODULE.MERIT_ASSESSMENT,
              ) &&
                merits.length === validAssessedMeritIds.length) ||
                !merits?.length),
          },
        ]
      : []),
    {
      label: STEPPER_LABELS.GSB1.LABEL,
      description: STEPPER_LABELS.GSB1.DESCRIPTION,
      hasDone: hasDoneArr.includes(
        ASSESSMENT_DETAIL_MODULE.ADMISSION_REQUIREMENT,
      ),
    },
    {
      label: STEPPER_LABELS.REFEREE_REPORT.LABEL,
      description: STEPPER_LABELS.REFEREE_REPORT.DESCRIPTION,
      hasDone: hasDoneArr.includes(
        ASSESSMENT_DETAIL_MODULE.REFEREE_ELIGIBILITY,
      ),
    },
    ...(isUPAF
      ? [
          {
            label: STEPPER_LABELS.INTERVIEW_PANEL.LABEL,
            description: STEPPER_LABELS.INTERVIEW_PANEL.DESCRIPTION,
            hasDone: hasDoneArr.includes(
              ASSESSMENT_DETAIL_MODULE.INTERVIEW_PANEL,
            ),
          },
        ]
      : []),
    {
      label: STEPPER_LABELS.DECLARATION.LABEL,
      description: STEPPER_LABELS.DECLARATION.DESCRIPTION,
      hasDone: hasDoneArr.includes(ASSESSMENT_DETAIL_MODULE.DECLARATION),
    },
  ];

  const sidebarData: SidebarData[] = sidebarModule.map(item => {
    const disabled = !isUPAF && item === ASSESSMENT_MODULE.EVALUATION;
    return {
      id: item,
      name: item,
      disabled,
      hoverText: disabled
        ? PAGE_TEXT.DRC_ASSESSMENT_DETAILS.DISABLE_EVALUATION_TIP
        : undefined,
    };
  });

  const handleJumpToNext = (): void => {
    setActivatedStep(activatedStep + 1);
  };
  const refetch = (): void => {
    refetchDsrcAssessment();
    refetchNominationEvaluation();
  };
  const assessmentDetailedModulesMap: Partial<
    Record<ASSESSMENT_DETAIL_MODULE, React.ReactNode>
  > = {
    [ASSESSMENT_DETAIL_MODULE.ACADEMIC_QUALIFICATION]: isUPSF ? (
      <AcademicAssessments
        academicQualificationIds={academicQualificationIds}
        ifRecommendForHKPFS={recommendForHKPFS}
        refetchAssessment={refetch}
        onJumpToNext={handleJumpToNext}
      />
    ) : null,
    [ASSESSMENT_DETAIL_MODULE.PUBLICATION_ASSESSMENT]: isUPSF ? (
      <Accordion
        className="w-full pt-4"
        defaultExpanded
        title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_B.TITLE}
        details={
          <PublicationAssessments
            publicationIds={
              publications?.map(item => item?.publicationId ?? '') ?? []
            }
            refetchAssessment={refetch}
            onJumpToNext={handleJumpToNext}
          />
        }
      />
    ) : null,
    [ASSESSMENT_DETAIL_MODULE.MERIT_ASSESSMENT]: isUPSF ? (
      <Accordion
        className="w-full pt-4"
        defaultExpanded
        title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_C.TITLE}
        details={
          <MeritAssessments
            meritIds={merits}
            refetchAssessment={refetch}
            onJumpToNext={handleJumpToNext}
          />
        }
      />
    ) : null,
    [ASSESSMENT_DETAIL_MODULE.SCORE_ON_APPLICANT]: isUPSF ? (
      <ScoresOnApplicant onJumpToNext={handleJumpToNext} />
    ) : null,

    [ASSESSMENT_DETAIL_MODULE.ADMISSION_REQUIREMENT]: (
      <Admission entryYear={entryYear} onJumpToNext={handleJumpToNext} />
    ),
    [ASSESSMENT_DETAIL_MODULE.REFEREE_ELIGIBILITY]: (
      <AcademicRefereeConfirmation
        refereeTickMode={RefereeTickMode.DirectorConfirm}
        refetchAssessment={refetch}
        onJumpToNext={handleJumpToNext}
      />
    ),
    [ASSESSMENT_DETAIL_MODULE.INTERVIEW_PANEL]: isUPAF ? (
      <Accordion
        className="w-full !mt-4"
        disableExpand
        defaultExpanded
        title={PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PANEL_INFORMATION.TITLE}
        details={
          <PanelInterview
            refetchAssessment={refetch}
            isViewMode={isScholarshipInterviewFinished}
            onJumpToNext={handleJumpToNext}
          />
        }
      />
    ) : null,

    [ASSESSMENT_DETAIL_MODULE.DECLARATION]: (
      <DeclarationAssessments
        returnedRemarks={returnedRemarks}
        refetchAssessment={refetch}
        recommendForHKPFS={recommendForHKPFS}
      />
    ),
  };

  const assessmentModulesMap: Partial<
    Record<
      ASSESSMENT_MODULE,
      { component: React.ReactNode; reachable: boolean }
    >
  > = {
    [ASSESSMENT_MODULE.PARTICULARS]: {
      component: <ApplicantParticulars showGoDetail />,
      reachable: true,
    },

    [ASSESSMENT_MODULE.DORPGS_ASSESSMENT]: {
      component:
        assessmentDetailedModulesMap[
          Object.keys(map)[
            Object.values(map).indexOf(activatedStep)
          ] as keyof typeof assessmentDetailedModulesMap
        ],
      reachable: true,
    },

    [ASSESSMENT_MODULE.EVALUATION]: {
      component: (
        <Evaluation
          scene={
            isScholarshipInterviewFinished
              ? EVALUATION_SCENE.ENDORSEMENT
              : EVALUATION_SCENE.DORPG_PREVIEW
          }
          applicationId={applicationId}
          scholarshipNomination={scholarshipNomination}
          refetchEvaluation={refetch}
        />
      ),
      reachable: isUPAF,
    },
  };

  const activatedModule: React.ReactNode | undefined =
    assessmentModulesMap[activatedModuleId]?.component;

  const moduleComponent = activatedModule ?? activatedModuleId;
  const moduleTitle = activatedModuleId;
  const ifShowStepper: boolean =
    moduleTitle === PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.TITLE;

  if (!assessmentModulesMap[activatedModuleId]?.reachable) {
    activatedModuleId = ASSESSMENT_MODULE.DORPGS_ASSESSMENT;
    searchParams.set('am', activatedModuleId);
  }

  const handleBack = (): void => {
    window.location.href = '/internal/dorpgs-homepage';
  };

  const handleDetailChange = (newDetail: ASSESSMENT_DETAIL_MODULE): void => {
    handleStepperChange(Object(map)[newDetail] ?? 0);
  };

  const handleStepperChange = (newStep: number): void => {
    setActivatedStep(newStep);
  };

  const submitDsrcAssessment = useSubmitDsrcAssessment();
  const recomputeGrading = useRecomputeGrading();
  const confirmSubmitDRCAssessment = async (): Promise<void> => {
    setDialogOpen(false);
    try {
      if (isUPAF) {
        await recomputeGrading({
          variables: { applicationId },
        });
      }

      await submitDsrcAssessment({
        variables: { applicationId },
      });
      setDialogDetails(dialogMappings[PAGE_TEXT.CLOSE]);
      setDialogOpen(true);
    } catch (e: any) {
      setAlertDetails({
        alertContent: `Error Occurs.${e}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const handleGoToReturn = (): void => {
    setDialogOpen(false);
    if (activatedModuleId !== ASSESSMENT_MODULE.DORPGS_ASSESSMENT) {
      searchParams.set('am', ASSESSMENT_MODULE.DORPGS_ASSESSMENT);
      // Update the URL with new Module
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }

    handleDetailChange(ASSESSMENT_DETAIL_MODULE.ADMISSION_REQUIREMENT);
  };

  const handleSubmitAssessment = (): void => {
    const unsavedSteps = drcAssessmentValidation.filter(
      step => !step.condition,
    );

    if (unsavedSteps.length > 0) {
      setAlertDetails({
        alertContent: unsavedSteps[0].message,
        alertType: 'warning',
      });
      setOpenAlert(true);
      handleDetailChange(unsavedSteps[0].stepKey);
      return;
    }

    if (isUPAF && isScholarshipInterviewFinished && admissionApproved) {
      // if this application comes from interview panel
      // then should check if dorpgs has finished the scholarship evaluation
      const {
        academicProgramme,
        endorsement,
        eligibilityCheck = [],
      } = scholarshipNomination;
      const today = new Date();

      const hasTickedOverdueFunding = eligibilityCheck.some(fundingInfo => {
        const validFundingDeadline = !checkInvalidDate(
          fundingInfo.fundingDeadline,
        );
        return (
          // if already frozen status, they skip check deadline;
          !FROZEN_STATUS.includes(fundingInfo?.status) &&
          fundingInfo.checked &&
          validFundingDeadline &&
          dayjs(fundingInfo.fundingDeadline).isBefore(dayjs(today))
        );
      });
      if (!academicProgramme || !endorsement) {
        setAlertDetails({
          alertContent:
            'Please complete the Academic Programme Category (APC) and Nomination to GS in Scholarship Evaluation.',
          alertType: 'warning',
        });
        setOpenAlert(true);
        handleChangeActivated(ASSESSMENT_MODULE.EVALUATION);
        return;
      }

      if (hasTickedOverdueFunding) {
        setAlertDetails({
          alertContent:
            'Please uncheck the expired funding checkbox in the Scholarship Evaluation section. ',
          alertType: 'warning',
        });
        setOpenAlert(true);
        handleChangeActivated(ASSESSMENT_MODULE.EVALUATION);
        return;
      }
    }

    setDialogDetails(dialogMappings[PAGE_TEXT.SUBMIT]);
    setDialogOpen(true);
  };

  const handleJumpViewRemarks = (): void => {
    handleDetailChange(ASSESSMENT_DETAIL_MODULE.DECLARATION);
    setDialogOpen(false);
  };

  if (error ?? assessmentDetailError ?? fundingError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            error?.message +
            assessmentDetailError?.message +
            fundingError?.message,
          alertType: 'error',
        }}
      />
    );
  }

  // submit contentText
  const {
    TITLE_APPROVE,
    TITLE_RETURN,
    TITLE_REJECT,
    CONTENT,
    CONTENT_REJ,
    CONTENT_RET,
  } = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.SUBMIT_CONFIRM;

  const dialogMappings: Record<string, DialogDetails> = {
    [PAGE_TEXT.SUBMIT]: {
      title: admissionApproved
        ? TITLE_APPROVE
        : dsrcDecision === DSRC_DECISION_TYPE.REJECT
          ? TITLE_REJECT
          : TITLE_RETURN,
      content: admissionApproved
        ? CONTENT
        : dsrcDecision === DSRC_DECISION_TYPE.REJECT
          ? CONTENT_REJ
          : CONTENT_RET,
      confirmAction: PAGE_TEXT.SUBMIT,
      cancelAction: PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: confirmSubmitDRCAssessment,
    },
    // on submit successfully
    [PAGE_TEXT.CLOSE]: {
      title: '',
      content: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.SUBMIT_SUCCESS,
      confirmAction: PAGE_TEXT.BACK,
      handleDialogConfirmAction: handleBack,
    },
    // on open when it is a returned dsrc
    [PAGE_TEXT.PREVIEW_DIALOG.TITLE]: {
      title: PAGE_TEXT.PREVIEW_DIALOG.TITLE,
      content: PAGE_TEXT.PREVIEW_DIALOG.CONTENT,
      confirmAction: PAGE_TEXT.OK,
      handleDialogConfirmAction: handleJumpViewRemarks,
    },
  };

  useEffect(() => {
    const drcRelatedRemark = returnedRemarks.filter(
      rmk =>
        rmk.fromRole === UserRole.DO_RPG_S ||
        rmk.toRole.includes(UserRole.DO_RPG_S),
    );
    if (!loading && drcRelatedRemark.length) {
      setDialogDetails(dialogMappings[PAGE_TEXT.PREVIEW_DIALOG.TITLE]);
      setDialogOpen(true);
      handleChangeActivated(ASSESSMENT_MODULE.DORPGS_ASSESSMENT);
    }
  }, [loading]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Dialog
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        width={'700px'}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={handleCloseDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
      <PageContainer>
        <div className="sidebar-area">
          <AssessmentSidebar
            onChangeActivated={handleChangeActivated}
            currentActivatedModule={activatedModuleId}
            collapsible={true}
            sidebarData={sidebarData}
            drawerWidth={244}
            drawerInnerWidth="15rem"
            buttonField={
              <div className="flex pt-12 justify-center w-full">
                <div className="flex flex-col gap-4 w-1/2">
                  <Button variant="outlined" onClick={handleBack}>
                    {PAGE_TEXT.BACK}
                  </Button>
                  {activatedModuleId !== ASSESSMENT_MODULE.PARTICULARS && (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleSubmitAssessment}
                      >
                        {PAGE_TEXT.SUBMIT}
                      </Button>
                      <Button
                        sx={{ textTransform: 'none' }}
                        variant="text"
                        onClick={handleGoToReturn}
                      >
                        {PAGE_TEXT.RETURN_REJECT}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            }
          />
        </div>

        <Container>
          <h1>{moduleTitle}</h1>
          <div className="flex flex-row">
            <div className="form-area">{moduleComponent}</div>
            {ifShowStepper && (
              <div className="mt-6 px-2">
                <Stepper
                  activeStep={activatedStep}
                  steps={assessmentSteps}
                  onChange={handleStepperChange}
                />
              </div>
            )}
          </div>
        </Container>
      </PageContainer>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;

  .sidebar-area {
    width: min-content;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  .form-area {
    margin: 1rem;
    display: flex;
    flex: 1;
    min-width: 800px;
  }
`;
